/**
 * Pokémon type colors — Future Cartridge palette (PR 6 of TODOs #24
 * harmonization). Values mirror tokens.css's `--type-*` custom
 * properties exactly so the JS-side rgba tints (PartyOrbs orb
 * backdrop-tint at 0.08 alpha) match the CSS-side type-tinted
 * surfaces (draft cards, slot card lead orb, PokemonFullSheet IV
 * fills, action button focus borders).
 *
 * The pre-migration values were the canonical Bulbapedia / pkmn.help
 * "warm" palette (fire #EE8130, water #6390F0); Future Cartridge
 * dials them more saturated (fire #FF4D29, water #2E9EFF) for the
 * "saturated chromatic identity against gray-glass chrome" thesis
 * (design doc rev 6 §"Mood thesis").
 *
 * KEEP IN SYNC WITH tokens.css `--type-*`. If a future PR adjusts
 * type colors, update BOTH this map and the CSS tokens — the JS-side
 * orb tint and the CSS-side card halo are user-perceived as one
 * material; drift between them reads as a render bug.
 */
export const TYPE_COLORS: Record<string, string> = {
  normal:   '#B6B5A2',
  fire:     '#FF4D29',
  water:    '#2E9EFF',
  electric: '#FFD126',
  grass:    '#5AD66E',
  ice:      '#7AD9DA',
  fighting: '#C13C46',
  poison:   '#B450C7',
  ground:   '#E5B956',
  flying:   '#A5BEF0',
  psychic:  '#FF5C82',
  bug:      '#A4BB39',
  rock:     '#B89B47',
  ghost:    '#6E5BA6',
  dragon:   '#7B5BFA',
  dark:     '#5F4F44',
  steel:    '#B8BAC4',
  fairy:    '#FF93C8',
};

/**
 * Resolve a type name to its rgba color at the given alpha. Case-
 * insensitive — accepts 'Electric' or 'electric' interchangeably since
 * @pkmn/dex returns Title Case and most other places use lowercase.
 * Falls back to the Normal color for unknown / missing types so the orb
 * still renders a consistent faint tint.
 */
export function typeColorRgba(typeName: string | undefined, alpha = 0.08): string {
  const key = (typeName ?? 'normal').toLowerCase();
  const hex = TYPE_COLORS[key] ?? TYPE_COLORS.normal!;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
